/**
 * Small formatting helpers. Kept outside react-query and components
 * so they're trivially testable and reusable.
 */

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const delta = Date.now() - then;
  if (delta < 0) {
    // future — typically a "next run at" timestamp
    const inFuture = -delta;
    if (inFuture < 60_000) return 'in under a minute';
    if (inFuture < 3_600_000) return `in ${Math.floor(inFuture / 60_000)}m`;
    if (inFuture < 86_400_000) return `in ${Math.floor(inFuture / 3_600_000)}h`;
    return `in ${Math.floor(inFuture / 86_400_000)}d`;
  }
  if (delta < 60_000) return 'just now';
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m ago`;
  if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)}h ago`;
  return `${Math.floor(delta / 86_400_000)}d ago`;
}

export function truncate(value: string | null | undefined, max = 64): string {
  if (!value) return '—';
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}…`;
}

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

/** USD cents → "$12.34". */
export function formatCents(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return USD.format(cents / 100);
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Human copy for the common audit-schedule cron shapes. Raw cron in the
 *  product UI reads as debug chrome — translate the shapes we actually
 *  generate ("0 3 * * *" → "Daily at 03:00 UTC"); anything exotic falls
 *  back to null so callers can show the raw string as a last resort. */
export function humanizeCron(cron: string | null | undefined): string | null {
  if (!cron) return null;
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [min, hour, dom, mon, dow] = parts as [string, string, string, string, string];
  const pad = (v: string) => v.padStart(2, '0');
  const isNum = (v: string) => /^\d+$/.test(v);
  // "M */N * * *" — every N hours
  const everyN = hour.match(/^\*\/(\d+)$/);
  if (isNum(min) && everyN && dom === '*' && mon === '*' && dow === '*') {
    const n = Number(everyN[1]);
    return n === 1 ? 'Hourly' : `Every ${n} hours`;
  }
  // "M H * * *" — daily
  if (isNum(min) && isNum(hour) && dom === '*' && mon === '*' && dow === '*') {
    return `Daily at ${pad(hour)}:${pad(min)} UTC`;
  }
  // "M H * * D" — weekly on one weekday
  if (isNum(min) && isNum(hour) && dom === '*' && mon === '*' && /^[0-6]$/.test(dow)) {
    return `${WEEKDAYS[Number(dow)]}s at ${pad(hour)}:${pad(min)} UTC`;
  }
  return null;
}

export function hostOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

export function formatMs(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

export function formatScore(score: number | null | undefined): string {
  if (score == null) return '—';
  return String(Math.round(score));
}

/**
 * Compact byte formatting — KB up to 1 MB, MB beyond. Mirrors the
 * conventions used by Chrome DevTools' network panel + Lighthouse
 * report so users see numbers they already recognise. Returns "—"
 * for null/undefined (consistent with the other format.ts helpers).
 */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

/** Integer counter formatting ("1,234"). */
export function formatCount(n: number | null | undefined): string {
  if (n == null) return '—';
  return n.toLocaleString();
}

/** Cumulative Layout Shift — unitless, up to 3 decimals, no trailing zeros. */
export function formatCls(cls: number | null | undefined): string {
  if (cls == null) return '—';
  return String(Number(cls.toFixed(3)));
}

export function scoreColor(score: number | null | undefined): string {
  if (score == null) return 'text-[var(--color-mute)]';
  if (score >= 90) return 'text-[var(--color-success)]';
  if (score >= 50) return 'text-[var(--color-warning)]';
  return 'text-[var(--color-danger)]';
}

/** Core Web Vitals thresholds (mobile). */
export function lcpColor(ms: number | null | undefined): string {
  if (ms == null) return 'text-[var(--color-mute)]';
  if (ms <= 2500) return 'text-[var(--color-success)]';
  if (ms <= 4000) return 'text-[var(--color-warning)]';
  return 'text-[var(--color-danger)]';
}

export function clsColor(cls: number | null | undefined): string {
  if (cls == null) return 'text-[var(--color-mute)]';
  if (cls <= 0.1) return 'text-[var(--color-success)]';
  if (cls <= 0.25) return 'text-[var(--color-warning)]';
  return 'text-[var(--color-danger)]';
}

export function inpColor(ms: number | null | undefined): string {
  if (ms == null) return 'text-[var(--color-mute)]';
  if (ms <= 200) return 'text-[var(--color-success)]';
  if (ms <= 500) return 'text-[var(--color-warning)]';
  return 'text-[var(--color-danger)]';
}

// ─── Bands ─────────────────────────────────────────────────────────────
//
// Lighthouse / CWV bands surfaced as semantic strings + matching CSS
// custom-property names. The `Color` helpers above return Tailwind
// `text-...` classes (for the big value); the helpers below return the
// raw CSS variable string for SVG `fill` / `stroke` on bars + dots.

export type MetricBand = 'good' | 'needs-improvement' | 'poor' | 'unknown';

export function lcpBand(ms: number | null | undefined): MetricBand {
  if (ms == null) return 'unknown';
  if (ms <= 2500) return 'good';
  if (ms <= 4000) return 'needs-improvement';
  return 'poor';
}

export function clsBand(cls: number | null | undefined): MetricBand {
  if (cls == null) return 'unknown';
  if (cls <= 0.1) return 'good';
  if (cls <= 0.25) return 'needs-improvement';
  return 'poor';
}

export function inpBand(ms: number | null | undefined): MetricBand {
  if (ms == null) return 'unknown';
  if (ms <= 200) return 'good';
  if (ms <= 500) return 'needs-improvement';
  return 'poor';
}

/** Approximation; TTFB doesn't have official CWV bands. */
export function ttfbBand(ms: number | null | undefined): MetricBand {
  if (ms == null) return 'unknown';
  if (ms <= 800) return 'good';
  if (ms <= 1800) return 'needs-improvement';
  return 'poor';
}

/** web.dev's documented FCP thresholds (1800 / 3000 ms). */
export function fcpBand(ms: number | null | undefined): MetricBand {
  if (ms == null) return 'unknown';
  if (ms <= 1800) return 'good';
  if (ms <= 3000) return 'needs-improvement';
  return 'poor';
}

/** Lighthouse's Total Blocking Time scoring thresholds (mobile). */
export function tbtBand(ms: number | null | undefined): MetricBand {
  if (ms == null) return 'unknown';
  if (ms <= 200) return 'good';
  if (ms <= 600) return 'needs-improvement';
  return 'poor';
}

/** Lighthouse's Speed Index scoring thresholds (mobile), in ms. */
export function siBand(ms: number | null | undefined): MetricBand {
  if (ms == null) return 'unknown';
  if (ms <= 3400) return 'good';
  if (ms <= 5800) return 'needs-improvement';
  return 'poor';
}

export function scoreBand(score: number | null | undefined): MetricBand {
  if (score == null) return 'unknown';
  if (score >= 90) return 'good';
  if (score >= 50) return 'needs-improvement';
  return 'poor';
}

/** Raw CSS var for SVG fill/stroke. */
export function bandFill(band: MetricBand): string {
  switch (band) {
    case 'good':
      return 'var(--color-success)';
    case 'needs-improvement':
      return 'var(--color-warning)';
    case 'poor':
      return 'var(--color-danger)';
    case 'unknown':
      return 'var(--color-mute)';
  }
}

/** A tint of a band colour, mixed toward white. Theme-aware via
 *  `--heat-tile-scale`: on dark the full-strength mix reads as a luminous
 *  tile (the SpeedCurve heatmap look, paired with dark ink); on light the
 *  scale shrinks it to a soft pastel tint (paired with the band's own dark
 *  text via `--heat-ink-mix` — see HeatmapTable.cellStyle). */
export function bandTint(band: MetricBand, strengthPct = 88): string {
  return `color-mix(in srgb, ${bandFill(band)} calc(${strengthPct}% * var(--heat-tile-scale, 1)), #ffffff)`;
}

/** Chart-area fill for a band — full colour on dark, softened toward
 *  white on light (`--chart-fill-mix`) so large filled areas (stacked
 *  distributions, choropleths) don't overpower the light page. */
export function bandChartFill(band: MetricBand): string {
  return `color-mix(in srgb, ${bandFill(band)} var(--chart-fill-mix, 100%), #ffffff)`;
}

/** Tailwind text class for the big mono value. */
export function bandText(band: MetricBand): string {
  switch (band) {
    case 'good':
      return 'text-[var(--color-success)]';
    case 'needs-improvement':
      return 'text-[var(--color-warning)]';
    case 'poor':
      return 'text-[var(--color-danger)]';
    case 'unknown':
      return 'text-[var(--color-mute)]';
  }
}

/** Human label for `navigator.connection.effectiveType`. Deliberately
 *  says "-class": the browser reports a *speed* bucket derived from
 *  round-trip time, not the medium — someone on fast Wi-Fi and someone
 *  on 4G LTE both report `4g`. Returns '' for unknown so callers can
 *  drop the field rather than print a placeholder. */
export function networkLabel(effectiveType: string | null | undefined): string {
  switch (effectiveType) {
    case '4g':
      return '4G-class';
    case '3g':
      return '3G-class';
    case '2g':
      return '2G-class';
    case 'slow-2g':
      return 'Slow 2G';
    default:
      return '';
  }
}
